/* ==========================================================================
   Land Media — shared behaviour for all lm-* sections
   Reveal, split headings, count-up, tabs, sliders, hero deck + live charts,
   silk waves, globe, chatbot loop, word rotator, parallax shapes,
   card spotlight, magnetic buttons, cursor, scroll progress.
   ========================================================================== */
(function () {
  if (window.__lmBrand) return;
  window.__lmBrand = true;

  var html = document.documentElement;
  html.classList.add('lm-js');
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var finePointer = window.matchMedia('(pointer: fine)').matches;
  // Cursor-driven extras (custom cursor, magnetic buttons, card spotlight) need a mouse AND a desktop-sized screen.
  var deskFx = window.matchMedia('(pointer: fine) and (min-width: 1024px)');
  // Phones / touch tablets: canvases draw fewer points. They used to skip every other frame
  // (~30 fps), which looked choppy; drawing is now cheap enough to run every frame.
  var lite = !finePointer || window.innerWidth < 1024;
  var FRAME_LITE = 0;

  /* ---------- Smooth mode: lighter effects on slower devices ----------
     html.lm-smooth is set in the <head> (remembered / modest device) or here, when this browser
     keeps missing frames: during the first seconds on the page, or while scrolling. In smooth mode
     the decorative loops stop (background canvases hold a still frame, endless CSS decorations
     pause, no frosted glass); content motion stays: reveals, card changes, scroll effects, marquee. */
  function smoothOn() { return html.classList.contains('lm-smooth'); }
  var smoothFns = [];
  function onSmooth(fn) { smoothFns.push(fn); }
  function pauseDecor() {
    if (!document.getAnimations) return;
    document.getAnimations().forEach(function (a) {
      try {
        if (a.playState !== 'finished' && a.effect && !(a.effect.target && a.effect.target.closest && a.effect.target.closest('.lm-chat, .lm-proj, [data-lm-film], .lm-loader')) && a.effect.getComputedTiming().iterations === Infinity) a.pause();
      } catch (e) { /* ignore */ }
    });
  }
  function enableSmooth() {
    if (smoothOn() && enableSmooth.done) return;
    enableSmooth.done = true;
    html.classList.add('lm-smooth');
    try { localStorage.setItem('lmSmooth', '1'); } catch (e) {}
    pauseDecor();
    smoothFns.forEach(function (fn) { try { fn(); } catch (e) { /* keep going */ } });
    window.dispatchEvent(new CustomEvent('lm:smooth'));
  }
  window.__lmSmooth = smoothOn;
  if (!reduceMotion) {
    if (smoothOn()) {
      // Decorations that start later (reveals, sections coming into view) get paused as they appear.
      setTimeout(function () { enableSmooth(); }, 0);
    }
    // Frame sampler: runs for a few seconds after load, and again whenever the page scrolls.
    var sampling = false, sLast = 0, sCount = 0, sSlow = 0, sUntil = 0;
    var sampleFrame = function (t) {
      if (sLast) {
        var dt = t - sLast;
        if (dt < 250) { sCount++; if (dt > 22) sSlow++; } // ignore gaps from hidden tabs / long tasks at load
      }
      sLast = t;
      if (sCount >= 60) {
        if (sSlow / sCount > 0.25) { enableSmooth(); sampling = false; return; }
        sCount = 0; sSlow = 0;
      }
      if (performance.now() < sUntil && !smoothOn()) requestAnimationFrame(sampleFrame);
      else sampling = false;
    };
    var sampleFor = function (ms) {
      if (smoothOn() || document.hidden) return;
      sUntil = Math.max(sUntil, performance.now() + ms);
      if (!sampling) { sampling = true; sLast = 0; requestAnimationFrame(sampleFrame); }
    };
    window.addEventListener('load', function () { setTimeout(function () { sampleFor(4000); }, 1500); });
    window.addEventListener('scroll', function () { sampleFor(1500); }, { passive: true });
  }

  function once(el, key) {
    if (el['__lm' + key]) return false;
    el['__lm' + key] = true;
    return true;
  }

  // While the homepage intro film covers the page nothing underneath needs to animate, so
  // visibility callbacks (which start the canvas / marquee loops) are held and replayed with
  // their latest state once the film lifts.
  var filmOn = html.classList.contains('lm-film-on'), filmQueue = [];
  function releaseFilm() {
    if (!filmOn) return;
    filmOn = false;
    var q = filmQueue; filmQueue = [];
    q.forEach(function (fn) { fn(); });
  }
  if (filmOn) {
    window.addEventListener('lm:film-done', releaseFilm);
    // Safety net: theme.liquid's timeout can drop the class without the film's event.
    (function watch() { if (!filmOn) return; if (!html.classList.contains('lm-film-on')) releaseFilm(); else setTimeout(watch, 1500); })();
  }
  function onVisible(el, cb, opts) {
    if (!('IntersectionObserver' in window)) { cb(true); return; }
    var held = false, last = false;
    new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        last = e.isIntersecting;
        if (filmOn) {
          if (!held) { held = true; filmQueue.push(function () { cb(last); }); }
          return;
        }
        cb(e.isIntersecting);
      });
    }, opts || { threshold: 0 }).observe(el);
  }

  /* ---------- Count-up ---------- */
  function formatNumber(value, decimals) {
    return value.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  }
  function countUp(el) {
    var raw = el.getAttribute('data-lm-count');
    var end = parseFloat(raw);
    if (isNaN(end)) return;
    var decimals = (raw.split('.')[1] || '').length;
    if (reduceMotion) { el.textContent = formatNumber(end, decimals); return; }
    var start = null;
    function step(now) {
      if (start === null) start = now;
      var p = Math.min((now - start) / 2000, 1);
      el.textContent = formatNumber(end * (1 - Math.pow(1 - p, 4)), decimals);
      if (p < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  /* ---------- Split headings into words ---------- */
  // .lm-words headings arrive split from Liquid and animate from CSS at the first paint (lm-brand.css).
  function splitWords(el) {
    if (reduceMotion || el.classList.contains('lm-words') || !once(el, 'Split')) return;
    var i = 0;
    (function walk(node) {
      Array.prototype.slice.call(node.childNodes).forEach(function (child) {
        if (child.nodeType === 3) {
          var frag = document.createDocumentFragment();
          child.textContent.split(/(\s+)/).forEach(function (part) {
            if (!part) return;
            if (/^\s+$/.test(part)) { frag.appendChild(document.createTextNode(part)); return; }
            var w = document.createElement('span');
            w.className = 'lm-word';
            var inner = document.createElement('span');
            inner.className = 'lm-word__in';
            inner.style.setProperty('--wi', i++);
            inner.textContent = part;
            w.appendChild(inner);
            frag.appendChild(w);
          });
          child.parentNode.replaceChild(frag, child);
        } else if (child.nodeType === 1 && child.tagName !== 'BR') {
          walk(child);
        }
      });
    })(el);
    el.classList.add('lm-split');
  }

  /* ---------- Reveal on scroll ---------- */
  function reveal(el) {
    el.classList.add('is-in');
    if (el.hasAttribute('data-lm-count')) countUp(el);
  }
  var revealObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          revealObserver.unobserve(entry.target);
          reveal(entry.target);
        });
      }, { threshold: 0.15, rootMargin: '0px 0px -40px 0px' })
    : null;

  function initReveal(root) {
    root.querySelectorAll('.lm h1, .lm .lm-h2').forEach(splitWords);
    root.querySelectorAll('.lm-reveal:not(.is-in), .lm-split:not(.is-in), [data-lm-count]:not(.is-in)').forEach(function (el) {
      if (el.hasAttribute('data-lm-count') && !reduceMotion) el.textContent = '0';
      if (revealObserver) revealObserver.observe(el); else reveal(el);
    });
  }

  /* ---------- Tabs (with optional autoplay) ---------- */
  function initTabs(root) {
    root.querySelectorAll('[data-lm-tabs]').forEach(function (wrap) {
      if (!once(wrap, 'Tabs')) return;
      var tabs = wrap.querySelectorAll('[role="tab"]');
      var panels = wrap.querySelectorAll('[role="tabpanel"]');
      var delay = parseInt(wrap.getAttribute('data-lm-autoplay'), 10) || 0;
      var current = 0, timer = null, visible = false, stopped = false;

      function select(index, focus) {
        current = index;
        tabs.forEach(function (tab, i) {
          var active = i === index;
          tab.setAttribute('aria-selected', active ? 'true' : 'false');
          tab.tabIndex = active ? 0 : -1;
          tab.classList.remove('is-timing');
          if (active && focus) tab.focus();
        });
        panels.forEach(function (panel, i) {
          panel.hidden = i !== index;
          if (i === index) panel.querySelectorAll('.lm-reveal').forEach(function (el) {
            el.classList.remove('is-in'); void el.offsetWidth; reveal(el);
          });
        });
        schedule();
      }
      function schedule() {
        clearTimeout(timer);
        if (!delay || stopped || !visible || reduceMotion || tabs.length < 2) return;
        var tab = tabs[current];
        void tab.offsetWidth;
        tab.classList.add('is-timing');
        timer = setTimeout(function () { select((current + 1) % tabs.length, false); }, delay);
      }
      function stop() { stopped = true; clearTimeout(timer); tabs.forEach(function (t) { t.classList.remove('is-timing'); }); }

      tabs.forEach(function (tab, i) {
        tab.addEventListener('click', function () { stop(); select(i, false); });
        tab.addEventListener('keydown', function (e) {
          if (e.key === 'ArrowRight') { e.preventDefault(); stop(); select((i + 1) % tabs.length, true); }
          if (e.key === 'ArrowLeft') { e.preventDefault(); stop(); select((i - 1 + tabs.length) % tabs.length, true); }
        });
      });
      wrap.style.setProperty('--lm-autoplay', delay + 'ms');
      onVisible(wrap, function (v) { visible = v; if (v) schedule(); else clearTimeout(timer); }, { threshold: 0.3 });
    });
  }

  /* ---------- Horizontal slider (with optional autoplay) ---------- */
  function initSliders(root) {
    root.querySelectorAll('[data-lm-slider]').forEach(function (wrap) {
      if (!once(wrap, 'Slider')) return;
      var track = wrap.querySelector('[data-lm-track]');
      var prev = wrap.querySelector('[data-lm-prev]');
      var next = wrap.querySelector('[data-lm-next]');
      if (!track) return;
      var delay = parseInt(wrap.getAttribute('data-lm-autoplay'), 10) || 0;
      var timer = null, hover = false, visible = false;
      function stepSize() {
        var card = track.firstElementChild;
        return card ? card.getBoundingClientRect().width + 24 : track.clientWidth;
      }
      function atEnd() { return track.scrollLeft + track.clientWidth >= track.scrollWidth - 4; }
      function update() {
        if (prev) prev.disabled = track.scrollLeft <= 4;
        if (next) next.disabled = atEnd();
      }
      function auto() {
        clearInterval(timer);
        if (!delay || reduceMotion) return;
        timer = setInterval(function () {
          if (hover || !visible) return;
          if (atEnd()) track.scrollTo({ left: 0, behavior: 'smooth' });
          else track.scrollBy({ left: stepSize(), behavior: 'smooth' });
        }, delay);
      }
      if (prev) prev.addEventListener('click', function () { track.scrollBy({ left: -stepSize(), behavior: 'smooth' }); auto(); });
      if (next) next.addEventListener('click', function () { track.scrollBy({ left: stepSize(), behavior: 'smooth' }); auto(); });
      wrap.addEventListener('pointerenter', function () { hover = true; });
      wrap.addEventListener('pointerleave', function () { hover = false; });
      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      onVisible(wrap, function (v) { visible = v; });
      update();
      auto();
    });
  }

  /* ---------- Count a text value like "PKR 12.4M" or "48,210" up to its number ---------- */
  function countText(el, target, animate) {
    var m = /^(\D*?)([\d][\d,]*\.?\d*)(.*)$/.exec(target || '');
    if (!m || !animate || reduceMotion) { el.textContent = target || ''; return; }
    var prefix = m[1], raw = m[2], suffix = m[3];
    var end = parseFloat(raw.replace(/,/g, ''));
    var decimals = (raw.split('.')[1] || '').length, commas = raw.indexOf(',') > -1;
    var start = null, from = 0;
    function fmt(v) {
      var s = v.toFixed(decimals);
      if (commas) s = Number(s).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
      return prefix + s + suffix;
    }
    (function step(now) {
      if (start === null) start = now;
      var p = Math.min((now - start) / 1100, 1);
      el.textContent = fmt(from + (end - from) * (1 - Math.pow(1 - p, 3)));
      if (p < 1) requestAnimationFrame(step); else el.textContent = target;
    })(performance.now());
  }

  /* ---------- Hero showcase: browser window wipes between screenshots, KPI cards update ---------- */
  function initShowcase(root) {
    root.querySelectorAll('[data-lm-show]').forEach(function (show) {
      if (!once(show, 'Show')) return;
      var screens = Array.prototype.slice.call(show.querySelectorAll('.lm-show__screen'));
      var tabs = Array.prototype.slice.call(show.querySelectorAll('[data-show-go]'));
      var n = screens.length;
      if (!n) return;
      var stage = show.querySelector('.lm-show__stage');
      var url = show.querySelector('[data-show-url]');
      var cards = [1, 2, 3].map(function (k) {
        return {
          card: show.querySelector('.lm-show__kpi--' + k),
          label: show.querySelector('[data-kpi-label="' + k + '"]'),
          value: show.querySelector('[data-kpi-value="' + k + '"]'),
          delta: show.querySelector('[data-kpi-delta="' + k + '"]')
        };
      });
      var interval = parseInt(show.getAttribute('data-interval'), 10) || 6000;
      var idx = 0, timer = null, visible = false, hover = false, shownOnce = false;
      show.style.setProperty('--lm-interval', interval + 'ms');

      function setCards(s, animate) {
        cards.forEach(function (c, i) {
          if (!c.card) return;
          var k = i + 1;
          var label = s.getAttribute('data-k' + k + '-label'), value = s.getAttribute('data-k' + k + '-value');
          c.card.hidden = !label && !value;
          if (c.label) c.label.textContent = label || '';
          if (c.value) countText(c.value, value, animate);
          if (c.delta) c.delta.textContent = s.getAttribute('data-k' + k + '-delta') || '';
          if (animate) { c.card.classList.remove('is-pop'); void c.card.offsetWidth; c.card.classList.add('is-pop'); }
        });
      }
      function restartProgress() {
        show.classList.remove('is-running');
        void show.offsetWidth;
        if (n > 1 && !reduceMotion) show.classList.add('is-running');
      }
      function go(i) {
        if (i === idx) return;
        var prev = screens[idx];
        prev.classList.remove('is-active');
        prev.classList.add('is-leaving');
        setTimeout(function () { prev.classList.remove('is-leaving'); }, 1050);
        idx = (i + n) % n;
        screens[idx].classList.add('is-active');
        tabs.forEach(function (t, k) {
          t.classList.toggle('is-active', k === idx);
          t.setAttribute('aria-selected', k === idx ? 'true' : 'false');
        });
        if (url) url.textContent = screens[idx].getAttribute('data-url') || '';
        setCards(screens[idx], true);
        restartProgress();
        schedule();
      }
      function schedule() {
        clearTimeout(timer);
        show.classList.toggle('is-paused', hover || !visible);
        if (reduceMotion || n < 2 || hover || !visible) return;
        timer = setTimeout(function () { go((idx + 1) % n); }, interval);
      }
      tabs.forEach(function (t, k) { t.addEventListener('click', function () { go(k); }); });

      // Mouse tilt + floating cards at different depths
      var kpis = show.querySelectorAll('[data-depth]');
      if (finePointer && !reduceMotion) {
        show.addEventListener('pointermove', function (e) {
          var r = show.getBoundingClientRect();
          var x = (e.clientX - r.left) / r.width - 0.5, y = (e.clientY - r.top) / r.height - 0.5;
          stage.style.transform = 'perspective(1600px) rotateY(' + (-12 + x * 12).toFixed(2) + 'deg) rotateX(' + (6 - y * 9).toFixed(2) + 'deg)';
          kpis.forEach(function (c) {
            var d = parseFloat(c.getAttribute('data-depth')) || 20;
            c.style.translate = (x * d).toFixed(1) + 'px ' + (y * d).toFixed(1) + 'px';
          });
        });
      }
      show.addEventListener('pointerenter', function () { hover = true; schedule(); });
      show.addEventListener('pointerleave', function () {
        hover = false;
        stage.style.transform = '';
        kpis.forEach(function (c) { c.style.translate = ''; });
        schedule();
      });

      onVisible(show, function (v) {
        visible = v;
        if (v && !shownOnce) { shownOnce = true; setCards(screens[idx], true); restartProgress(); }
        schedule();
      }, { threshold: 0.25 });
      setCards(screens[0], false);
    });
  }

  /* ---------- 3D perspective particle wave (light hero background) ---------- */
  function initWave(root) {
    root.querySelectorAll('canvas[data-lm-wave]').forEach(function (canvas) {
      if (!once(canvas, 'Wave')) return;
      var ctx = canvas.getContext('2d');
      if (!ctx) return;
      var w = 0, h = 0, cols = 0, rows = 0, dpr = Math.min(window.devicePixelRatio || 1, lite ? 1.5 : 2), lastT = 0;
      var mouse = { x: 0.5, y: 0.5, tx: 0.5, ty: 0.5 }, running = false, raf = null, palette = [];
      function lerpColor(t) {
        var a = [0, 123, 255], b = [139, 92, 246], c = [255, 138, 0];
        var p = t < 0.5 ? [a, b, t * 2] : [b, c, (t - 0.5) * 2];
        return [p[0][0] + (p[1][0] - p[0][0]) * p[2], p[0][1] + (p[1][1] - p[0][1]) * p[2], p[0][2] + (p[1][2] - p[0][2]) * p[2]].map(Math.round);
      }
      function resize() {
        w = canvas.clientWidth; h = canvas.clientHeight;
        canvas.width = w * dpr; canvas.height = h * dpr;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        cols = w < 760 ? 38 : (lite ? 64 : 90);
        rows = w < 760 ? 18 : (lite ? 24 : 30);
        palette = [];
        for (var c = 0; c < cols; c++) palette.push('rgb(' + lerpColor(c / (cols - 1)).join(',') + ')');
      }
      function draw(t) {
        mouse.x += (mouse.tx - mouse.x) * 0.04;
        mouse.y += (mouse.ty - mouse.y) * 0.04;
        ctx.clearRect(0, 0, w, h);
        var fov = Math.max(w, 700) * 0.62, horizon = h * 0.14, depthScale = h * 0.5, shift = (mouse.x - 0.5) * 1.2;
        for (var c = 0; c < cols; c++) {
          var xw = (c / (cols - 1) - 0.5) * 14;
          var mx = Math.sin((xw - (mouse.x - 0.5) * 10) * 0.35) * (mouse.y - 0.2) * 0.35;
          ctx.fillStyle = palette[c];
          for (var r = rows - 1; r >= 0; r--) {
            var z = 1 + r * 0.24, near = 1 - r / (rows - 1);
            var sx = w / 2 + ((xw + shift) / z) * fov * 0.5;
            if (sx < -10 || sx > w + 10) continue;
            var y = Math.sin(xw * 0.55 + t * 0.0009 + r * 0.32) * 0.42 + Math.sin(r * 0.5 - t * 0.0007 + xw * 0.22) * 0.3 + mx;
            var sy = horizon + ((1.6 - y) / z) * depthScale;
            if (sy > h + 10) continue;
            var size = 1 + 2.4 * near;
            ctx.globalAlpha = 0.1 + 0.62 * Math.pow(near, 1.4);
            ctx.fillRect(sx - size / 2, sy - size / 2, size, size);
          }
        }
        ctx.globalAlpha = 1;
      }
      function loop(t) {
        if (smoothOn()) { running = false; draw(t); return; } // smooth mode: one still frame
        if (!lite || t - lastT >= FRAME_LITE) { lastT = t; draw(t); }
        if (running) raf = requestAnimationFrame(loop);
      }
      resize();
      window.addEventListener('resize', function () { resize(); if (!running) draw(0); });
      var host = canvas.closest('section') || canvas.parentElement;
      host.addEventListener('pointermove', function (e) {
        var r = host.getBoundingClientRect();
        mouse.tx = (e.clientX - r.left) / r.width;
        mouse.ty = (e.clientY - r.top) / r.height;
      });
      if (reduceMotion) { draw(0); return; }
      onVisible(canvas, function (v) {
        if (v && !running) { running = true; raf = requestAnimationFrame(loop); }
        if (!v) { running = false; cancelAnimationFrame(raf); }
      });
    });
  }

  /* ---------- Silk waves canvas ---------- */
  function initSilk(root) {
    root.querySelectorAll('canvas[data-lm-silk]').forEach(function (canvas) {
      if (!once(canvas, 'Silk')) return;
      var ctx = canvas.getContext('2d');
      if (!ctx) return;
      var lines = parseInt(canvas.getAttribute('data-lines'), 10) || 26;
      if (lite) lines = Math.max(10, Math.round(lines * 0.6));
      var baseY = parseFloat(canvas.getAttribute('data-base')) || 0.78;
      var amp = parseFloat(canvas.getAttribute('data-amp')) || 1;
      var w = 0, h = 0, dpr = Math.min(window.devicePixelRatio || 1, lite ? 1.5 : 2), lastT = 0;
      var mouse = { x: 0.5, y: 0.5, tx: 0.5, ty: 0.5 };
      var running = false, raf = null, grad;

      function resize() {
        w = canvas.clientWidth; h = canvas.clientHeight;
        canvas.width = w * dpr; canvas.height = h * dpr;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        grad = ctx.createLinearGradient(0, 0, w, 0);
        grad.addColorStop(0, '#007BFF');
        grad.addColorStop(0.5, '#8B5CF6');
        grad.addColorStop(1, '#FF8A00');
      }
      function draw(t) {
        mouse.x += (mouse.tx - mouse.x) * 0.05;
        mouse.y += (mouse.ty - mouse.y) * 0.05;
        ctx.clearRect(0, 0, w, h);
        ctx.strokeStyle = grad;
        var step = Math.max(10, w / 120);
        for (var i = 0; i < lines; i++) {
          var p = i / (lines - 1);
          ctx.globalAlpha = 0.08 + 0.55 * (1 - Math.abs(p - 0.5) * 2);
          ctx.lineWidth = 1.1;
          ctx.beginPath();
          for (var x = -step; x <= w + step; x += step) {
            var nx = x / w;
            var pull = Math.exp(-Math.pow((nx - mouse.x) * 3.2, 2)) * (mouse.y - 0.5) * h * 0.16;
            var y = h * baseY
              + Math.sin(x * 0.0021 + t * 0.00055 + p * 1.7) * h * 0.13 * amp * (0.55 + p * 0.7)
              + Math.sin(x * 0.0009 - t * 0.0003 + p * 2.6) * h * 0.09 * amp
              + (p - 0.5) * h * 0.2 * (0.6 + 0.4 * Math.sin(x * 0.0016 + t * 0.0004))
              + pull;
            if (x === -step) ctx.moveTo(x, y); else ctx.lineTo(x, y);
          }
          ctx.stroke();
        }
        ctx.globalAlpha = 1;
      }
      function loop(t) {
        if (smoothOn()) { running = false; draw(t); return; } // smooth mode: one still frame
        if (!lite || t - lastT >= FRAME_LITE) { lastT = t; draw(t); }
        if (running) raf = requestAnimationFrame(loop);
      }
      resize();
      window.addEventListener('resize', function () { resize(); if (!running) draw(0); });
      var host = canvas.parentElement;
      host.addEventListener('pointermove', function (e) {
        var r = canvas.getBoundingClientRect();
        mouse.tx = (e.clientX - r.left) / r.width;
        mouse.ty = (e.clientY - r.top) / r.height;
      });
      if (reduceMotion) { draw(0); return; }
      onVisible(canvas, function (v) {
        if (v && !running) { running = true; raf = requestAnimationFrame(loop); }
        if (!v) { running = false; cancelAnimationFrame(raf); }
      });
    });
  }

  /* ---------- Dotted globe with market arcs (drag to spin) ---------- */
  function initGlobes(root) {
    root.querySelectorAll('canvas[data-lm-globe]').forEach(function (canvas) {
      if (!once(canvas, 'Globe')) return;
      var ctx = canvas.getContext('2d');
      if (!ctx) return;
      var markets = [];
      try { markets = JSON.parse(canvas.getAttribute('data-lm-globe')) || []; } catch (e) { markets = []; }
      var rad = Math.PI / 180;
      function toVec(lat, lon) {
        return [Math.cos(lat * rad) * Math.sin(lon * rad), Math.sin(lat * rad), Math.cos(lat * rad) * Math.cos(lon * rad)];
      }
      markets.forEach(function (m) { m.v = toVec(+m.lat || 0, +m.lon || 0); });
      var hq = markets.filter(function (m) { return m.hq; })[0] || markets[0];

      var N = lite ? 700 : 1100, pts = [], lastT = 0;
      var golden = Math.PI * (3 - Math.sqrt(5));
      for (var i = 0; i < N; i++) {
        var y = 1 - (i / (N - 1)) * 2, r = Math.sqrt(1 - y * y), th = golden * i;
        pts.push([Math.cos(th) * r, y, Math.sin(th) * r]);
      }
      // Arcs: HQ → each other market
      var arcs = markets.filter(function (m) { return m !== hq; }).map(function (m) {
        var a = hq.v, b = m.v;
        var dot = Math.max(-1, Math.min(1, a[0] * b[0] + a[1] * b[1] + a[2] * b[2]));
        var omega = Math.acos(dot), so = Math.sin(omega) || 1, seg = [];
        for (var k = 0; k <= 48; k++) {
          var t = k / 48, s1 = Math.sin((1 - t) * omega) / so, s2 = Math.sin(t * omega) / so;
          var lift = 1 + Math.sin(Math.PI * t) * Math.min(0.26, 0.1 + omega * 0.1);
          seg.push([(a[0] * s1 + b[0] * s2) * lift, (a[1] * s1 + b[1] * s2) * lift, (a[2] * s1 + b[2] * s2) * lift]);
        }
        return seg;
      });

      var dpr = Math.min(window.devicePixelRatio || 1, 2), size = 0, halo = null;
      // Near-side dots go into a few depth buckets, each drawn as one path (one fill) per frame.
      var BUCKETS = 6, bucketFill = [], bx = [], by = [];
      for (var bi = 0; bi < BUCKETS; bi++) { bucketFill.push('rgba(11,27,61,' + (0.18 + ((bi + 0.5) / BUCKETS) * 0.5).toFixed(3) + ')'); bx.push([]); by.push([]); }
      var rotY = -(hq ? +hq.lon : 0) * rad + 0.35, rotX = 0.32, velY = 0.0022;
      var dragging = false, lastX = 0, lastY = 0, running = false, raf = null;

      function resize() {
        size = canvas.clientWidth;
        canvas.width = size * dpr; canvas.height = size * dpr;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        var R0 = size * 0.385, c0 = size / 2;
        halo = ctx.createRadialGradient(c0, c0, R0 * 0.7, c0, c0, R0 * 1.25);
        halo.addColorStop(0, 'rgba(139,92,246,0.10)');
        halo.addColorStop(1, 'rgba(139,92,246,0)');
      }
      function project(v) {
        var cy = Math.cos(rotY), sy = Math.sin(rotY), cx = Math.cos(rotX), sx = Math.sin(rotX);
        var x1 = v[0] * cy + v[2] * sy, z1 = -v[0] * sy + v[2] * cy;
        var y2 = v[1] * cx - z1 * sx, z2 = v[1] * sx + z1 * cx;
        var R = size * 0.385;
        return [size / 2 + x1 * R, size / 2 - y2 * R, z2];
      }
      function draw(t) {
        ctx.clearRect(0, 0, size, size);
        var R = size * 0.385, c = size / 2;
        // halo
        ctx.fillStyle = halo;
        ctx.beginPath(); ctx.arc(c, c, R * 1.25, 0, Math.PI * 2); ctx.fill();
        // dots: far side as one path of squares, near side in depth buckets
        var cy = Math.cos(rotY), sy = Math.sin(rotY), cx = Math.cos(rotX), sx = Math.sin(rotX), k, i;
        for (k = 0; k < BUCKETS; k++) { bx[k].length = 0; by[k].length = 0; }
        ctx.fillStyle = 'rgba(11,27,61,0.06)';
        ctx.beginPath();
        for (i = 0; i < pts.length; i++) {
          var v = pts[i], x1 = v[0] * cy + v[2] * sy, z1 = -v[0] * sy + v[2] * cy;
          var y2 = v[1] * cx - z1 * sx, z2 = v[1] * sx + z1 * cx, px = c + x1 * R, py = c - y2 * R;
          if (z2 < 0) { ctx.rect(px, py, 1.2, 1.2); continue; }
          k = Math.min(BUCKETS - 1, Math.floor(z2 * BUCKETS));
          bx[k].push(px); by[k].push(py);
        }
        ctx.fill();
        for (k = 0; k < BUCKETS; k++) {
          if (!bx[k].length) continue;
          var rr = 0.8 + ((k + 0.5) / BUCKETS) * 1.1, xs = bx[k], ys = by[k];
          ctx.fillStyle = bucketFill[k];
          ctx.beginPath();
          for (i = 0; i < xs.length; i++) { ctx.moveTo(xs[i] + rr, ys[i]); ctx.arc(xs[i], ys[i], rr, 0, Math.PI * 2); }
          ctx.fill();
        }
        // arcs
        arcs.forEach(function (seg, ai) {
          var proj = seg.map(project);
          var lg = ctx.createLinearGradient(proj[0][0], proj[0][1], proj[proj.length - 1][0], proj[proj.length - 1][1]);
          lg.addColorStop(0, '#FF8A00'); lg.addColorStop(1, '#8B5CF6');
          ctx.strokeStyle = lg; ctx.lineWidth = 2;
          ctx.beginPath();
          var started = false;
          proj.forEach(function (q, k) {
            var r3 = Math.hypot(seg[k][0], seg[k][1], seg[k][2]);
            var visible = q[2] > -0.05 || Math.hypot(q[0] - c, q[1] - c) > R * Math.min(1, r3 * 0.98);
            if (!visible) { started = false; return; }
            if (!started) { ctx.moveTo(q[0], q[1]); started = true; } else ctx.lineTo(q[0], q[1]);
          });
          ctx.stroke();
          // travelling comet
          var tt = ((t * 0.00035) + ai * 0.27) % 1;
          var q = proj[Math.floor(tt * (proj.length - 1))];
          if (q[2] > -0.05) {
            // soft orange glow + white core (shadowBlur was a blur on every frame)
            ctx.fillStyle = 'rgba(255,138,0,.22)';
            ctx.beginPath(); ctx.arc(q[0], q[1], 8, 0, Math.PI * 2); ctx.fill();
            ctx.fillStyle = 'rgba(255,138,0,.4)';
            ctx.beginPath(); ctx.arc(q[0], q[1], 5, 0, Math.PI * 2); ctx.fill();
            ctx.fillStyle = '#fff';
            ctx.beginPath(); ctx.arc(q[0], q[1], 3, 0, Math.PI * 2); ctx.fill();
          }
        });
        // markers
        ctx.font = '700 12px "Plus Jakarta Sans", system-ui, sans-serif';
        var placed = [], shown = [];
        markets.forEach(function (m, mi) {
          var p = project(m.v);
          if (p[2] < 0) return;
          shown.push([m, p]);
          placed.push([p[0] - 8, p[1] - 12, 16]);
          var pulse = (t * 0.0012 + mi * 0.5) % 1;
          ctx.strokeStyle = m === hq ? 'rgba(255,138,0,' + (1 - pulse) + ')' : 'rgba(0,123,255,' + (1 - pulse) + ')';
          ctx.lineWidth = 2;
          ctx.beginPath(); ctx.arc(p[0], p[1], 5 + pulse * 14, 0, Math.PI * 2); ctx.stroke();
          ctx.fillStyle = m === hq ? '#FF8A00' : '#007BFF';
          ctx.beginPath(); ctx.arc(p[0], p[1], 5, 0, Math.PI * 2); ctx.fill();
          ctx.fillStyle = '#fff';
          ctx.beginPath(); ctx.arc(p[0], p[1], 2, 0, Math.PI * 2); ctx.fill();
        });
        shown.forEach(function (mp) {
          var m = mp[0], p = mp[1];
          var label = m.n || '';
          var tw = ctx.measureText(label).width, bw = tw + 16;
          // Right of the dot by default; move left / up / down when it would cover another label.
          var spots = [[p[0] + 12, p[1] - 12], [p[0] - 12 - bw, p[1] - 12], [p[0] - bw / 2, p[1] - 40], [p[0] - bw / 2, p[1] + 16]];
          var spot = spots[0];
          for (var si = 0; si < spots.length; si++) {
            var s = spots[si], hit = placed.some(function (b) { return s[0] < b[0] + b[2] + 4 && s[0] + bw + 4 > b[0] && s[1] < b[1] + 28 && s[1] + 28 > b[1]; });
            if (!hit) { spot = s; break; }
          }
          placed.push([spot[0], spot[1], bw]);
          ctx.globalAlpha = Math.min(1, p[2] * 3);
          ctx.fillStyle = '#0B1B3D';
          roundRect(ctx, spot[0], spot[1], bw, 24, 12); ctx.fill();
          ctx.fillStyle = '#fff';
          ctx.fillText(label, spot[0] + 8, spot[1] + 16);
          ctx.globalAlpha = 1;
        });
      }
      function roundRect(c, x, y, w, h, r) {
        c.beginPath();
        c.moveTo(x + r, y); c.arcTo(x + w, y, x + w, y + h, r); c.arcTo(x + w, y + h, x, y + h, r);
        c.arcTo(x, y + h, x, y, r); c.arcTo(x, y, x + w, y, r); c.closePath();
      }
      function loop(t) {
        if (smoothOn() && !dragging) { running = false; draw(t); return; } // smooth mode: still until dragged
        if (!lite || dragging || t - lastT >= FRAME_LITE) {
          if (!dragging) rotY += velY * (lastT ? Math.min(3, (t - lastT) / 16.7) : 1);
          lastT = t;
          draw(t);
        }
        if (running) raf = requestAnimationFrame(loop);
      }
      canvas.addEventListener('pointerdown', function (e) {
        dragging = true; lastX = e.clientX; lastY = e.clientY;
        canvas.setPointerCapture(e.pointerId);
        canvas.classList.add('is-dragging');
      });
      canvas.addEventListener('pointermove', function (e) {
        if (!dragging) return;
        rotY += (e.clientX - lastX) * 0.008;
        if (smoothOn()) { rotX = Math.max(-1.1, Math.min(1.1, rotX + (e.clientY - lastY) * 0.006)); lastX = e.clientX; lastY = e.clientY; draw(performance.now()); return; }
        rotX = Math.max(-1.1, Math.min(1.1, rotX + (e.clientY - lastY) * 0.006));
        lastX = e.clientX; lastY = e.clientY;
        if (!running) draw(performance.now());
      });
      function up() { dragging = false; canvas.classList.remove('is-dragging'); }
      canvas.addEventListener('pointerup', up);
      canvas.addEventListener('pointercancel', up);

      resize();
      window.addEventListener('resize', function () { resize(); if (!running) draw(0); });
      if (reduceMotion) { velY = 0; draw(0); return; }
      onVisible(canvas, function (v) {
        if (v && !running) { running = true; raf = requestAnimationFrame(loop); }
        if (!v) { running = false; cancelAnimationFrame(raf); }
      });
    });
  }

  /* ---------- Chatbot conversation loop ---------- */
  function initChats(root) {
    root.querySelectorAll('[data-lm-chat]').forEach(function (thread) {
      if (!once(thread, 'Chat') || reduceMotion) return;
      var pairs = [];
      try { pairs = JSON.parse(thread.getAttribute('data-lm-chat')) || []; } catch (e) { pairs = []; }
      pairs = pairs.filter(function (p) { return p.q && p.a; });
      if (!pairs.length) return;
      var i = 0, visible = false, busy = false;

      function wait(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }
      function bubble(cls, text) {
        var el = document.createElement('div');
        el.className = 'lm-software__msg ' + cls + ' is-pop';
        el.textContent = text || '';
        thread.appendChild(el);
        return el;
      }
      function typing() {
        var el = document.createElement('div');
        el.className = 'lm-software__typing is-pop';
        el.innerHTML = '<i></i><i></i><i></i>';
        thread.appendChild(el);
        return el;
      }
      function typeOut(el, text) {
        return new Promise(function (resolve) {
          var k = 0;
          (function step() {
            k += 2;
            el.textContent = text.slice(0, k);
            if (k < text.length) setTimeout(step, 18); else resolve();
          })();
        });
      }
      function run() {
        if (busy || !visible) return;
        busy = true;
        var pair = pairs[i % pairs.length];
        thread.classList.add('is-clearing');
        wait(400).then(function () {
          thread.innerHTML = '';
          thread.classList.remove('is-clearing');
          bubble('lm-software__msg--user', pair.q);
          return wait(700);
        }).then(function () {
          var t = typing();
          return wait(1300).then(function () { t.remove(); });
        }).then(function () {
          var b = bubble('lm-software__msg--bot', '');
          return typeOut(b, pair.a).then(function () {
            if (pair.s) {
              var s = document.createElement('span');
              s.className = 'lm-software__source is-pop';
              s.textContent = pair.s;
              b.appendChild(s);
            }
          });
        }).then(function () { return wait(3800); }).then(function () {
          i++; busy = false; run();
        });
      }
      onVisible(thread, function (v) { visible = v; if (v) run(); }, { threshold: 0.4 });
    });
  }

  /* ---------- Rotating words ---------- */
  function initRotators(root) {
    root.querySelectorAll('[data-lm-rotate]').forEach(function (el) {
      if (!once(el, 'Rotate') || reduceMotion) return;
      var words = [];
      try { words = JSON.parse(el.getAttribute('data-lm-rotate')) || []; } catch (e) { words = []; }
      words = words.map(function (w) { return String(w).trim(); }).filter(Boolean);
      if (words.length < 2) return;
      var i = 0;
      setInterval(function () {
        if (document.hidden) return;
        el.classList.add('is-out');
        setTimeout(function () {
          i = (i + 1) % words.length;
          el.textContent = words[i];
          el.classList.remove('is-out');
          el.classList.add('is-in-word');
          setTimeout(function () { el.classList.remove('is-in-word'); }, 500);
        }, 350);
      }, 2400);
    });
  }

  /* ---------- Magnetic buttons ---------- */
  function initMagnetic(root) {
    if (!finePointer || reduceMotion) return;
    root.querySelectorAll('.lm .lm-btn, .lm [data-lm-magnetic]').forEach(function (btn) {
      if (!once(btn, 'Magnet')) return;
      btn.addEventListener('pointermove', function (e) {
        if (!deskFx.matches || e.pointerType !== 'mouse') return;
        var r = btn.getBoundingClientRect();
        var x = e.clientX - r.left - r.width / 2;
        var y = e.clientY - r.top - r.height / 2;
        btn.style.translate = (x * 0.22).toFixed(1) + 'px ' + (y * 0.32).toFixed(1) + 'px';
      });
      btn.addEventListener('pointerleave', function () { btn.style.translate = ''; });
    });
  }

  /* ---------- Card spotlight (glow border follows the cursor) ---------- */
  if (finePointer) {
    document.addEventListener('pointermove', function (e) {
      if (!deskFx.matches) return;
      var card = e.target.closest && e.target.closest('.lm-card');
      if (!card) return;
      var r = card.getBoundingClientRect();
      card.style.setProperty('--mx', (e.clientX - r.left) + 'px');
      card.style.setProperty('--my', (e.clientY - r.top) + 'px');
    }, { passive: true });
  }

  /* ---------- Cursor spotlight on a section ([data-lm-spot] → --lm-sx/--lm-sy + .is-spot) ---------- */
  function initSpots(root) {
    if (!finePointer || reduceMotion) return;
    root.querySelectorAll('[data-lm-spot]').forEach(function (el) {
      if (!once(el, 'Spot')) return;
      el.addEventListener('pointermove', function (e) {
        if (!deskFx.matches) return;
        var r = el.getBoundingClientRect();
        el.style.setProperty('--lm-sx', (e.clientX - r.left).toFixed(0) + 'px');
        el.style.setProperty('--lm-sy', (e.clientY - r.top).toFixed(0) + 'px');
        el.classList.add('is-spot');
      }, { passive: true });
      el.addEventListener('pointerleave', function () { el.classList.remove('is-spot'); });
    });
  }

  /* ---------- Scroll velocity (drives marquee speed/skew) ---------- */
  // lastScrollY is read lazily: reading window.scrollY while this script loads forced the page's
  // first full layout inside the script task.
  var scrollVel = 0, scrollSign = 1, lastScrollY = null;
  window.addEventListener('scroll', function () {
    var y = window.scrollY;
    var d = lastScrollY === null ? 0 : y - lastScrollY;
    lastScrollY = y;
    scrollVel = d;
    if (d) scrollSign = d > 0 ? 1 : -1;
  }, { passive: true });

  /* Scroll-linked effects register here and run inside one rAF per scroll.
     Each one measures first and hands its style changes to write(): during the scroll frame the
     writes are queued and run after every effect has measured, so the page is laid out once per
     frame instead of once per effect. Outside the scroll frame write() runs immediately. */
  var scrollFns = [];
  var batching = false, writeQ = [];
  function write(fn) { if (batching) writeQ.push(fn); else fn(); }
  window.__lmWrite = write; // lm-reel.js / lm-hero.js / lm-results.js join the same batch when present
  function clamp01(v) { return v < 0 ? 0 : v > 1 ? 1 : v; }

  /* ---------- Hero sticks and gets covered by the next section ---------- */
  function initHeroStack(root) {
    if (reduceMotion) return;
    root.querySelectorAll('.lm-hero[data-lm-stack]').forEach(function (hero) {
      if (!once(hero, 'Stack')) return;
      var wrap = hero.closest('.shopify-section') || hero.parentElement;
      var next = wrap.nextElementSibling;
      if (!next) return;
      var enabled = false;
      // Only stack when the hero fits the screen; a taller hero (phones) would get its
      // lower part covered. Re-measured whenever the hero's size changes (fonts, images).
      function measure() {
        var fits = wrap.offsetHeight <= window.innerHeight * 1.05;
        enabled = fits;
        wrap.classList.toggle('lm-stack-wrap', fits);
        next.classList.toggle('lm-stack-next', fits);
        wrap.style.top = fits ? Math.min(0, window.innerHeight - wrap.offsetHeight) + 'px' : '';
        if (!fits) hero.style.setProperty('--lm-cover', '0');
      }
      measure();
      window.addEventListener('resize', measure);
      window.addEventListener('load', measure);
      if ('ResizeObserver' in window) new ResizeObserver(measure).observe(wrap);
      scrollFns.push(function (vh) {
        if (!enabled) return;
        var p = clamp01(1 - next.getBoundingClientRect().top / vh);
        write(function () {
          hero.style.setProperty('--lm-cover', p.toFixed(3));
          // Fully covered: stop painting the hero, so the sections sliding over it need no extra layers.
          wrap.classList.toggle('is-covered', p >= 0.999);
        });
      });
    });
  }

  /* ---------- Pinned horizontal scroll (process steps) ---------- */
  function initProcess(root) {
    root.querySelectorAll('[data-lm-process]').forEach(function (sec) {
      if (!once(sec, 'Process')) return;
      var pin = sec.querySelector('.lm-process__pin');
      var track = sec.querySelector('.lm-process__track');
      var bar = sec.querySelector('.lm-process__bar span');
      var count = sec.querySelector('.lm-process__count b');
      var cards = sec.querySelectorAll('.lm-process__card');
      var dist = 0, enabled = false, active = -1;

      function measure() {
        enabled = window.innerWidth >= 990 && !reduceMotion;
        sec.classList.toggle('is-pinned', enabled);
        if (!enabled) { sec.style.height = ''; track.style.transform = ''; return; }
        dist = Math.max(0, track.scrollWidth - pin.clientWidth);
        sec.style.height = (window.innerHeight + dist + window.innerHeight * 0.25) + 'px';
      }
      function update(vh) {
        if (!enabled) return;
        var r = sec.getBoundingClientRect();
        if (r.bottom < 0 || r.top > vh) return;
        var p = clamp01(-r.top / (sec.offsetHeight - vh));
        write(function () {
          track.style.transform = 'translate3d(' + (-p * dist).toFixed(1) + 'px,0,0)';
          if (bar) bar.style.transform = 'scaleX(' + p.toFixed(4) + ')';
          var i = Math.min(cards.length - 1, Math.round(p * (cards.length - 1)));
          if (i !== active) {
            active = i;
            cards.forEach(function (c, k) { c.classList.toggle('is-active', k === i); c.classList.toggle('is-past', k < i); });
            if (count) count.textContent = (i + 1 < 10 ? '0' : '') + (i + 1);
          }
        });
      }
      measure();
      window.addEventListener('resize', function () { measure(); update(window.innerHeight); });
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure);
      scrollFns.push(update);
    });
  }

  /* ---------- Journey: a glowing path draws itself through the steps on scroll ---------- */
  var SVGNS = 'http://www.w3.org/2000/svg';
  function initJourney(root) {
    root.querySelectorAll('[data-lm-journey]').forEach(function (sec) {
      if (!once(sec, 'Journey')) return;
      var track = sec.querySelector('.lm-journey__track');
      var svg = sec.querySelector('.lm-journey__svg');
      var base = svg.querySelector('.lm-journey__base');
      var line = svg.querySelector('.lm-journey__line');
      var glow = svg.querySelector('.lm-journey__glow');
      var comet = svg.querySelector('.lm-journey__comet');
      var grad = svg.querySelector('linearGradient');
      var steps = Array.prototype.slice.call(sec.querySelectorAll('.lm-journey__step'));
      var end = sec.querySelector('.lm-journey__end');
      var hudRing = sec.querySelector('.lm-journey__hud');
      var hudNum = sec.querySelector('.lm-journey__hud-num');
      var hudTitle = sec.querySelector('.lm-journey__hud-title');
      var titles = steps.map(function (s) { var t = s.querySelector('.lm-journey__title'); return t ? t.textContent : ''; });
      var stops = [], total = 0, table = [], active = -2, builtKey = '', buildRaf = 0;

      function centre(el) {
        var tr = track.getBoundingClientRect(), r = el.getBoundingClientRect();
        return { x: r.left - tr.left + r.width / 2, y: r.top - tr.top + r.height / 2 };
      }
      // Resize, fonts, load and the ResizeObserver can all fire in the same frame: build once.
      function scheduleBuild() {
        if (!buildRaf) buildRaf = requestAnimationFrame(function () { buildRaf = 0; build(); });
      }
      function build() {
        var nodes = steps.map(function (s) { return s.querySelector('.lm-journey__node'); });
        var endNode = end && end.querySelector('.lm-journey__node');
        if (endNode) nodes.push(endNode);
        stops = nodes.map(centre);
        if (!stops.length) return;
        var w = track.clientWidth, h = track.offsetHeight;
        // Nothing moved since the last build: keep the path (and the lookup table) as they are.
        var key = w + 'x' + h + '|' + stops.map(function (s) { return s.x.toFixed(1) + ',' + s.y.toFixed(1); }).join(';');
        if (key === builtKey) { update(window.innerHeight); return; }
        builtKey = key;
        svg.setAttribute('viewBox', '0 0 ' + w + ' ' + h);
        grad.setAttribute('y2', h);
        var mobile = window.innerWidth < 750;
        var amp = mobile ? 0 : Math.min(150, w * 0.13);
        var x0 = stops[0].x, y0 = stops[0].y;
        var d = 'M' + x0.toFixed(1) + ' 0 L' + x0.toFixed(1) + ' ' + y0.toFixed(1);
        // Length → point lookup, sampled from the curve maths directly (getPointAtLength in a loop
        // cost ~70 ms per build). Lengths are scaled to the browser's own total below.
        table = [[0, x0, 0], [y0, x0, y0]];
        var run = y0;
        for (var i = 1; i < stops.length; i++) {
          var a = stops[i - 1], b = stops[i], dy = b.y - a.y;
          // Swing out into the empty side of each row: away from card i-1, into the side card i leaves free.
          var side = steps[i - 1] && steps[i - 1].classList.contains('is-left') ? 1 : -1;
          var c1x = a.x + amp * side, c1y = a.y + dy * 0.35, c2x = b.x - amp * side, c2y = b.y - dy * 0.35;
          d += ' C' + c1x.toFixed(1) + ' ' + c1y.toFixed(1) + ' ' + c2x.toFixed(1) + ' ' + c2y.toFixed(1) +
               ' ' + b.x.toFixed(1) + ' ' + b.y.toFixed(1);
          var px = a.x, py = a.y;
          for (var k = 1; k <= 48; k++) {
            var t = k / 48, u = 1 - t, u2 = u * u, t2 = t * t;
            var qx = u2 * u * a.x + 3 * u2 * t * c1x + 3 * u * t2 * c2x + t2 * t * b.x;
            var qy = u2 * u * a.y + 3 * u2 * t * c1y + 3 * u * t2 * c2y + t2 * t * b.y;
            run += Math.sqrt((qx - px) * (qx - px) + (qy - py) * (qy - py));
            table.push([run, qx, qy]);
            px = qx; py = qy;
          }
        }
        [base, line, glow].forEach(function (p) { p.setAttribute('d', d); });
        total = line.getTotalLength();
        [line, glow].forEach(function (p) { p.style.strokeDasharray = total + ' ' + total; });
        var scale = run > 0 ? total / run : 1;
        table.forEach(function (row) { row[0] *= scale; });
        active = -2;
        update(window.innerHeight);
      }
      // The path only ever heads down the page, so y rises with length along it.
      function lengthAtY(y) {
        if (!table.length) return 0;
        if (y <= table[0][2]) return 0;
        for (var k = 1; k < table.length; k++) {
          if (table[k][2] >= y) {
            var a = table[k - 1], b = table[k], f = (y - a[2]) / ((b[2] - a[2]) || 1);
            return a[0] + (b[0] - a[0]) * f;
          }
        }
        return total;
      }
      function pointAt(L) {
        for (var k = 1; k < table.length; k++) {
          if (table[k][0] >= L) {
            var a = table[k - 1], b = table[k], f = (L - a[0]) / ((b[0] - a[0]) || 1);
            return { x: a[1] + (b[1] - a[1]) * f, y: a[2] + (b[2] - a[2]) * f };
          }
        }
        var last = table[table.length - 1];
        return { x: last[1], y: last[2] };
      }
      function update(vh) {
        if (!total) return;
        var tr = track.getBoundingClientRect();
        var live = tr.top < vh * 0.7 && tr.bottom > vh * 0.45;
        if (tr.bottom < -vh || tr.top > vh * 2) { write(function () { sec.classList.toggle('is-live', live); }); return; }
        write(function () { paint(tr, vh, live); });
      }
      function paint(tr, vh, live) {
        sec.classList.toggle('is-live', live);
        var eye = vh * 0.62 - tr.top;
        var L = reduceMotion ? total : lengthAtY(eye);
        var off = (total - L).toFixed(1);
        line.style.strokeDashoffset = off;
        glow.style.strokeDashoffset = off;
        var pt = pointAt(L);
        comet.setAttribute('transform', 'translate(' + pt.x.toFixed(1) + ' ' + pt.y.toFixed(1) + ')');
        var on = -1;
        stops.forEach(function (s, i) {
          var lit = reduceMotion || pt.y >= s.y - 4;
          if (lit) on = i;
          if (i < steps.length) steps[i].classList.toggle('is-on', lit);
          else if (end) end.classList.toggle('is-on', lit);
        });
        if (hudRing) hudRing.style.setProperty('--p', (L / total).toFixed(3));
        var idx = Math.max(0, Math.min(steps.length - 1, on));
        if (idx !== active && hudNum) {
          active = idx;
          hudNum.textContent = (idx + 1 < 10 ? '0' : '') + (idx + 1) + ' / ' + (steps.length < 10 ? '0' : '') + steps.length;
          if (hudTitle) hudTitle.textContent = titles[idx] || '';
        }
      }
      build();
      window.addEventListener('resize', scheduleBuild);
      window.addEventListener('load', scheduleBuild);
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(scheduleBuild);
      if ('ResizeObserver' in window) new ResizeObserver(scheduleBuild).observe(track);
      scrollFns.push(update);
    });
  }

  /* ---------- Footer wordmark: letters rise in, glow follows the cursor ---------- */
  function initFooterWord(root) {
    root.querySelectorAll('[data-lm-footer-word]').forEach(function (w) {
      if (!once(w, 'FooterWord')) return;
      if (revealObserver) revealObserver.observe(w); else w.classList.add('is-in');
      w.addEventListener('pointermove', function (e) {
        var r = w.getBoundingClientRect();
        w.style.setProperty('--fx', (e.clientX - r.left - r.width * 0.02) + 'px');
        w.style.setProperty('--fy', (e.clientY - r.top) + 'px');
      }, { passive: true });
    });
  }

  /* ---------- Stacking cards: each card sinks back as the next one slides over it ---------- */
  function initStackCards(root) {
    root.querySelectorAll('[data-lm-stack-cards]').forEach(function (list) {
      if (!once(list, 'StackCards')) return;
      var items = Array.prototype.slice.call(list.children);
      items.forEach(function (it) { if (revealObserver) revealObserver.observe(it); else it.classList.add('is-in'); });
      if (reduceMotion) return;
      scrollFns.push(function (vh) {
        var lr = list.getBoundingClientRect();
        if (lr.bottom < 0 || lr.top > vh) return;
        var covers = items.map(function (it, i) {
          var next = items[i + 1];
          if (!next) return 0;
          var stick = parseFloat(getComputedStyle(next).top) || 0;
          return clamp01((vh - next.getBoundingClientRect().top) / Math.max(1, vh - stick));
        });
        write(function () {
          items.forEach(function (it, i) { it.firstElementChild.style.setProperty('--cover', covers[i].toFixed(3)); });
        });
      });
    });
  }

  /* ---------- Contact form: custom option + WhatsApp copy of the enquiry ---------- */
  function initContactForms(root) {
    root.querySelectorAll('form[data-lm-contact]').forEach(function (form) {
      if (!once(form, 'Contact')) return;
      var select = form.querySelector('select[name="contact[service]"]');
      var custom = form.querySelector('[data-lm-custom-field]');
      var customInput = custom && custom.querySelector('input');
      function sync() {
        if (!select || !custom) return;
        var on = select.value === 'custom';
        custom.hidden = !on;
        customInput.required = on;
        if (on) customInput.focus({ preventScroll: true });
      }
      if (select) select.addEventListener('change', sync);
      if (customInput && customInput.value && select) { select.value = 'custom'; }
      sync();

      var wa = form.getAttribute('data-wa');
      var key = 'lm-wa-msg-' + form.id;
      // After a successful post the page reloads: offer the WhatsApp link again in case the pop-up was blocked.
      var again = form.querySelector('[data-lm-wa-again]');
      var saved = null;
      try { saved = sessionStorage.getItem(key); } catch (e) {}
      if (again && saved) { again.href = 'https://wa.me/' + wa + '?text=' + encodeURIComponent(saved); again.hidden = false; }
      try { sessionStorage.removeItem(key); } catch (e) {}

      if (!wa || form.getAttribute('data-wa-on') !== 'true') return;
      form.addEventListener('submit', function () {
        if (!form.checkValidity()) return;
        function val(n) { var el = form.querySelector('[name="contact[' + n + ']"]'); return el ? el.value.trim() : ''; }
        var service = val('service') === 'custom' ? 'Custom: ' + val('custom_request') : val('service');
        var lines = ['New enquiry from the website', '', 'Name: ' + val('name')];
        if (val('phone')) lines.push('Phone: ' + val('phone'));
        if (val('email')) lines.push('Email: ' + val('email'));
        if (service) lines.push('Interested in: ' + service);
        if (val('body')) lines.push('', val('body'));
        var msg = lines.join('\n');
        try { sessionStorage.setItem(key, msg); } catch (e) {}
        window.open('https://wa.me/' + wa + '?text=' + encodeURIComponent(msg), '_blank', 'noopener');
      });
    });
  }

  /* ---------- KPI sparkline (statement board) ---------- */
  function initSparks(root) {
    root.querySelectorAll('svg[data-lm-spark]').forEach(function (svg) {
      if (!once(svg, 'Spark')) return;
      var vals = (svg.getAttribute('data-lm-spark') || '').split(',').map(parseFloat).filter(function (v) { return !isNaN(v); });
      if (vals.length < 2) return;
      var W = 320, H = 110, pad = 12;
      var min = Math.min.apply(null, vals), max = Math.max.apply(null, vals), span = (max - min) || 1;
      var pts = vals.map(function (v, i) { return [i / (vals.length - 1) * W, pad + (1 - (v - min) / span) * (H - pad * 2)]; });
      var d = 'M' + pts[0][0] + ' ' + pts[0][1].toFixed(1);
      for (var i = 1; i < pts.length; i++) {
        var p0 = pts[i - 1], p1 = pts[i], cx = (p0[0] + p1[0]) / 2;
        d += ' C' + cx.toFixed(1) + ' ' + p0[1].toFixed(1) + ' ' + cx.toFixed(1) + ' ' + p1[1].toFixed(1) + ' ' + p1[0].toFixed(1) + ' ' + p1[1].toFixed(1);
      }
      svg.querySelector('.lm-kpi__line').setAttribute('d', d);
      svg.querySelector('.lm-kpi__area').setAttribute('d', d + ' L' + W + ' ' + H + ' L0 ' + H + ' Z');
      var dot = svg.parentElement.querySelector('.lm-kpi__dot');
      var last = pts[pts.length - 1];
      if (dot) { dot.style.left = (last[0] / W * 100) + '%'; dot.style.top = (last[1] / H * 100) + '%'; }
    });
  }

  /* ---------- Ribbon backgrounds: fade in, tilt with scroll ---------- */
  function initWaveBgs(root) {
    root.querySelectorAll('.lm-wavebg').forEach(function (bg) {
      if (!once(bg, 'WaveBg')) return;
      if (revealObserver) revealObserver.observe(bg); else bg.classList.add('is-in');
      if (reduceMotion) return;
      // Drifts with scroll and leans towards the cursor; eased every frame while on screen.
      var move = bg.querySelector('.lm-wavebg__move');
      var host = bg.parentElement;
      var visible = false, running = false;
      if (lite) {
        // Touch / small screens: no per-frame loop, just a gentle scroll-linked drift.
        scrollFns.push(function (vh) {
          var r = host.getBoundingClientRect();
          if (r.bottom < 0 || r.top > vh) return;
          var s = Math.max(-1.5, Math.min(1.5, (r.top + r.height / 2 - vh / 2) / vh));
          write(function () {
            move.style.translate = '0 ' + (s * -50).toFixed(1) + 'px';
            move.style.rotate = (s * -4).toFixed(2) + 'deg';
          });
        });
        return;
      }
      var tx = 0, ty = 0, tr = 0, x = 0, y = 0, rot = 0, sp = 0, px = 0, py = 0;
      if (finePointer) {
        host.addEventListener('pointermove', function (e) {
          var r = host.getBoundingClientRect();
          px = (e.clientX - r.left) / r.width * 2 - 1;
          py = (e.clientY - r.top) / r.height * 2 - 1;
        }, { passive: true });
        host.addEventListener('pointerleave', function () { px = 0; py = 0; });
      }
      function frame() {
        if (!visible) { running = false; return; }
        var r = host.getBoundingClientRect(), vh = window.innerHeight;
        sp = Math.max(-1.5, Math.min(1.5, (r.top + r.height / 2 - vh / 2) / vh));
        tx = px * 46; ty = sp * -90 + py * 30; tr = sp * -7 + px * 3;
        x += (tx - x) * 0.06; y += (ty - y) * 0.06; rot += (tr - rot) * 0.06;
        move.style.translate = x.toFixed(1) + 'px ' + y.toFixed(1) + 'px';
        move.style.rotate = rot.toFixed(2) + 'deg';
        requestAnimationFrame(frame);
      }
      onVisible(bg, function (v) {
        visible = v;
        if (v && !running) { running = true; requestAnimationFrame(frame); }
      });
    });
  }

  /* ---------- Growth timeline: floating screenshots, Jan → Dec on scroll ---------- */
  function initReel(root) {
    root.querySelectorAll('[data-lm-reel]').forEach(function (sec) {
      if (!once(sec, 'Reel')) return;
      var cards = Array.prototype.slice.call(sec.querySelectorAll('.lm-reel__card'));
      var n = cards.length;
      if (!n) return;
      var data = cards.map(function (c) {
        return {
          v: parseFloat(c.getAttribute('data-value')) || 0,
          m: c.getAttribute('data-month') || '',
          mf: c.getAttribute('data-month-full') || '',
          cap: c.getAttribute('data-caption') || ''
        };
      });
      var decimals = data.some(function (d) { return d.v % 1 !== 0; }) ? 1 : 0;
      var bg = sec.querySelector('[data-reel-bg]');
      var monthEl = sec.querySelector('[data-reel-month]');
      var valueEl = sec.querySelector('[data-reel-value]');
      var capEl = sec.querySelector('[data-reel-caption]');
      var clip = sec.querySelector('[data-reel-clip]');
      var dot = sec.querySelector('[data-reel-dot]');
      var spark = sec.querySelector('.lm-reel__spark');
      var progress = sec.querySelector('[data-reel-progress]');
      var buttons = Array.prototype.slice.call(sec.querySelectorAll('[data-reel-go]'));
      var shown = -1, lastP = -1;

      // Sparkline geometry in a 1000×100 box.
      var max = Math.max.apply(null, data.map(function (d) { return d.v; })) || 1;
      var pts = data.map(function (d, i) { return [n > 1 ? (i / (n - 1)) * 1000 : 500, 92 - (d.v / max) * 82]; });
      var line = pts.map(function (p, i) { return (i ? 'L' : 'M') + p[0].toFixed(1) + ',' + p[1].toFixed(1); }).join(' ');
      sec.querySelectorAll('[data-reel-line]').forEach(function (p) { p.setAttribute('d', line); });
      var area = sec.querySelector('[data-reel-area]');
      if (area) area.setAttribute('d', line + ' L1000,100 L0,100 Z');

      function measure() {
        var vh = window.innerHeight;
        var per = vh * (window.innerWidth < 750 ? 0.45 : 0.55);
        sec.style.height = (vh + per * (n - 1) + vh * 0.15) + 'px';
      }
      function geo() {
        if (!dot || !spark) return null;
        return { sr: spark.getBoundingClientRect(), fr: dot.offsetParent ? dot.offsetParent.getBoundingClientRect() : null };
      }
      function render(p, g) {
        if (g === undefined) g = geo();
        var pos = p * (n - 1);
        var mobile = window.innerWidth < 750;
        cards.forEach(function (c, i) {
          var d = i - pos, a = Math.abs(d);
          var x = d * (mobile ? 70 : 62);
          c.style.transform = 'translate(-50%, -50%) translateX(' + x.toFixed(2) + '%) translateZ(' + (-a * 220).toFixed(1) + 'px) rotateY(' + (-Math.max(-1.6, Math.min(1.6, d)) * 26).toFixed(2) + 'deg) scale(' + (1 - Math.min(a, 3) * 0.05).toFixed(3) + ')';
          c.style.opacity = a > 2.6 ? 0 : (1 - a * 0.28).toFixed(3);
          c.style.zIndex = String(100 - Math.round(a * 10));
          c.classList.toggle('is-current', a < 0.5);
        });
        // Number counts smoothly between months.
        var i0 = Math.floor(pos), i1 = Math.min(n - 1, i0 + 1), f = pos - i0;
        var v = data[i0].v + (data[i1].v - data[i0].v) * f;
        valueEl.textContent = v.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
        var idx = Math.round(pos);
        if (idx !== shown) {
          shown = idx;
          monthEl.textContent = data[idx].mf;
          capEl.textContent = data[idx].cap;
          if (bg) {
            bg.classList.add('is-swap');
            setTimeout(function () { bg.textContent = data[idx].m.toUpperCase(); bg.classList.remove('is-swap'); }, 160);
          }
          buttons.forEach(function (b, k) {
            b.classList.toggle('is-active', k === idx);
            b.classList.toggle('is-past', k < idx);
          });
        }
        if (clip) clip.setAttribute('width', (p * 1000).toFixed(1));
        if (progress) progress.style.transform = 'scaleX(' + p.toFixed(4) + ')';
        if (g && g.fr) {
          // SVG elements have no offsetLeft/Top, so measure against the dot's positioned parent.
          var x0 = pts[i0], x1 = pts[i1];
          var px = x0[0] + (x1[0] - x0[0]) * f, py = x0[1] + (x1[1] - x0[1]) * f;
          var sr = g.sr, fr = g.fr;
          dot.style.left = (sr.left - fr.left + (px / 1000) * sr.width) + 'px';
          dot.style.top = (sr.top - fr.top + (py / 100) * sr.height) + 'px';
        }
      }
      function update(vh) {
        var r = sec.getBoundingClientRect();
        if (r.bottom < 0 || r.top > vh) return;
        var p = n > 1 ? clamp01(-r.top / (sec.offsetHeight - vh)) : 0;
        if (Math.abs(p - lastP) < 0.0005) return;
        lastP = p;
        var g = geo();
        write(function () { render(p, g); });
      }
      buttons.forEach(function (b, k) {
        b.addEventListener('click', function () {
          var top = sec.getBoundingClientRect().top + window.scrollY;
          var range = sec.offsetHeight - window.innerHeight;
          window.scrollTo({ top: top + (n > 1 ? (k / (n - 1)) * range : 0) + 2, behavior: reduceMotion ? 'auto' : 'smooth' });
        });
      });
      measure();
      render(0);
      window.addEventListener('resize', function () { measure(); lastP = -1; update(window.innerHeight); });
      scrollFns.push(update);
    });
  }

  /* ---------- Services catalogue: live search + active category ---------- */
  function escapeHtml(s) { return s.replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
  function initCatalog(root) {
    root.querySelectorAll('[data-lm-catalog]').forEach(function (sec) {
      if (!once(sec, 'Catalog')) return;
      var input = sec.querySelector('[data-cat-search]');
      var found = sec.querySelector('[data-cat-found]');
      var empty = sec.querySelector('[data-cat-empty]');
      var cats = Array.prototype.slice.call(sec.querySelectorAll('[data-cat]'));
      var links = Array.prototype.slice.call(sec.querySelectorAll('[data-cat-link]'));
      var total = sec.querySelectorAll('[data-cat-item]').length;

      function filter() {
        var q = input.value.trim().toLowerCase(), n = 0;
        cats.forEach(function (cat, ci) {
          var title = (cat.querySelector('.lm-catalog__title') || {}).textContent || '';
          var catHit = q && title.toLowerCase().indexOf(q) > -1, m = 0;
          cat.querySelectorAll('[data-cat-item]').forEach(function (it) {
            var nameEl = it.querySelector('.lm-catalog__item-name');
            var text = nameEl.__lmText || (nameEl.__lmText = nameEl.textContent);
            var pos = q ? text.toLowerCase().indexOf(q) : -1;
            var hit = !q || catHit || pos > -1;
            it.hidden = !hit;
            it.classList.toggle('is-match', pos > -1);
            if (pos > -1) {
              nameEl.innerHTML = escapeHtml(text.slice(0, pos)) + '<mark>' + escapeHtml(text.slice(pos, pos + q.length)) + '</mark>' + escapeHtml(text.slice(pos + q.length));
            } else if (nameEl.firstElementChild) {
              nameEl.textContent = text;
            }
            if (hit) { m++; it.classList.add('is-in'); }
          });
          cat.hidden = m === 0;
          if (links[ci]) links[ci].classList.toggle('is-empty', m === 0);
          n += m;
        });
        found.textContent = q ? n + ' found' : total + ' services';
        if (empty) empty.hidden = n > 0;
      }
      if (input) input.addEventListener('input', filter);

      // Highlight the category currently in view; keep it visible in the mobile chip bar.
      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            if (!e.isIntersecting) return;
            var i = cats.indexOf(e.target);
            links.forEach(function (l, k) { l.classList.toggle('is-active', k === i); });
            var link = links[i], bar = link && link.closest('ol');
            if (bar && bar.scrollWidth > bar.clientWidth) bar.scrollTo({ left: link.offsetLeft - 16, behavior: 'smooth' });
          });
        }, { rootMargin: '-30% 0px -65% 0px' });
        cats.forEach(function (c) { io.observe(c); });
      }
    });
  }

  /* Brand gradient colour at t (0–1): blue → purple → orange */
  var GRAD = [[0, 123, 255], [139, 92, 246], [255, 138, 0]];
  function gradAt(t) {
    t = clamp01(t) * 2;
    var i = Math.min(1, Math.floor(t)), f = t - i, a = GRAD[i], b = GRAD[i + 1];
    return 'rgb(' + [0, 1, 2].map(function (k) { return Math.round(a[k] + (b[k] - a[k]) * f); }).join(',') + ')';
  }

  /* ---------- Words light up as you scroll (statement) ---------- */
  function initFill(root) {
    root.querySelectorAll('[data-lm-fill]').forEach(function (el) {
      if (!once(el, 'Fill')) return;
      var words = [];
      (function walk(node) {
        Array.prototype.slice.call(node.childNodes).forEach(function (child) {
          if (child.nodeType === 3) {
            var frag = document.createDocumentFragment();
            child.textContent.split(/(\s+)/).forEach(function (part) {
              if (!part) return;
              if (/^\s+$/.test(part)) { frag.appendChild(document.createTextNode(part)); return; }
              var w = document.createElement('span');
              w.className = 'lm-fill__w';
              w.textContent = part;
              words.push(w);
              frag.appendChild(w);
            });
            child.parentNode.replaceChild(frag, child);
          } else if (child.nodeType === 1) {
            walk(child);
          }
        });
      })(el);
      // Bold / italic phrases: spread the blue → purple → orange gradient across their words.
      el.querySelectorAll('em, strong, b').forEach(function (em) {
        var ws = em.querySelectorAll('.lm-fill__w');
        ws.forEach(function (w, i) {
          w.classList.add('is-grad');
          w.style.setProperty('--g1', gradAt(i / ws.length));
          w.style.setProperty('--g2', gradAt((i + 1) / ws.length));
        });
      });
      if (reduceMotion) { words.forEach(function (w) { w.classList.add('is-lit'); }); return; }
      var lit = 0;
      scrollFns.push(function (vh) {
        var r = el.getBoundingClientRect();
        var p = clamp01((vh * 0.85 - r.top) / (r.height + vh * 0.25));
        var n = Math.round(p * words.length);
        if (n === lit) return;
        var from = lit;
        lit = n;
        write(function () { for (var i = Math.min(n, from); i < Math.max(n, from); i++) words[i].classList.toggle('is-lit', i < n); });
      });
    });
  }

  /* ---------- Dark sections expand from a rounded card ---------- */
  function initInset(root) {
    if (reduceMotion) return;
    root.querySelectorAll('.lm-inset').forEach(function (sec) {
      if (!once(sec, 'Inset')) return;
      scrollFns.push(function (vh) {
        var r = sec.getBoundingClientRect();
        if (r.top > vh || r.bottom < 0) return;
        var v = clamp01((r.top - vh * 0.1) / (vh * 0.7)).toFixed(3);
        write(function () { sec.style.setProperty('--lm-in', v); });
      });
    });
  }

  /* ---------- Marquee driven by JS: speeds up, skews and flips with scroll ---------- */
  function initMarquees(root) {
    if (reduceMotion) return;
    root.querySelectorAll('.lm-marquee__track').forEach(function (track) {
      if (!once(track, 'Marquee')) return;
      var band = track.parentElement;
      var section = track.closest('.lm-marquee');
      var dir = band.classList.contains('lm-marquee__band--2') ? 1 : -1;
      var dur = parseFloat(getComputedStyle(section).getPropertyValue('--lm-speed')) || 35;
      var x = 0, skew = 0, visible = false, running = false, last = performance.now(), half = 0;
      track.classList.add('is-js');
      var hovered = false;
      band.addEventListener('pointerenter', function (e) { if (e.pointerType === 'mouse') hovered = true; });
      band.addEventListener('pointerleave', function () { hovered = false; });
      // Only animate while on screen; the loop stops itself when the band scrolls away.
      onVisible(band, function (v) {
        visible = v;
        if (v && !running) { running = true; half = track.scrollWidth / 2; last = performance.now(); requestAnimationFrame(loop); }
      });
      window.addEventListener('resize', function () { half = track.scrollWidth / 2; });
      function loop(now) {
        if (!visible) { running = false; return; }
        var dt = Math.min(64, now - last);
        last = now;
        if (!hovered) {
          if (!half) half = track.scrollWidth / 2;
          var boost = 1 + Math.min(10, Math.abs(scrollVel) * 0.12);
          x += (half / (dur * 1000)) * dt * boost * dir * scrollSign;
          if (x <= -half) x += half;
          if (x > 0) x -= half;
          skew += (Math.max(-12, Math.min(12, scrollVel * 0.35)) - skew) * 0.12;
          track.style.transform = 'translate3d(' + x.toFixed(1) + 'px,0,0) skewX(' + (-skew).toFixed(2) + 'deg)';
        }
        scrollVel *= 0.9;
        requestAnimationFrame(loop);
      }
    });
  }

  /* ---------- Scramble-decode eyebrow labels ---------- */
  var GLYPHS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789#%&*+=/<>';
  function scramble(el) {
    var final = el.getAttribute('data-lm-text') || el.textContent;
    el.setAttribute('data-lm-text', final);
    var frame = 0, total = 26;
    (function step() {
      frame++;
      var out = '';
      for (var i = 0; i < final.length; i++) {
        var ch = final[i];
        out += (ch === ' ' || frame > (i / final.length) * total + 6) ? ch : GLYPHS[Math.floor(Math.random() * GLYPHS.length)];
      }
      el.textContent = out;
      if (frame < total + 7) requestAnimationFrame(step); else el.textContent = final;
    })();
  }
  var scrambleObserver = ('IntersectionObserver' in window && !reduceMotion)
    ? new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          scrambleObserver.unobserve(e.target);
          scramble(e.target);
        });
      }, { threshold: 1 })
    : null;
  function initScramble(root) {
    if (!scrambleObserver) return;
    root.querySelectorAll('.lm .lm-eyebrow, .lm [data-lm-scramble]').forEach(function (el) {
      if (el.children.length || !once(el, 'Scramble')) return;
      // First-screen labels fade in from CSS (.lm-enter): decode them along with that entrance,
      // not after it when this script runs late on a slow phone.
      var a = el.classList.contains('lm-enter') && el.getAnimations ? el.getAnimations()[0] : null;
      if (a && (a.playState === 'finished' || a.currentTime > 300)) return;
      scrambleObserver.observe(el);
    });
  }

  /* ---------- Scroll parallax for floating shapes ---------- */
  var parallaxEls = [];
  function initParallax(root) {
    if (reduceMotion) return;
    root.querySelectorAll('[data-lm-depth]').forEach(function (el) {
      if (once(el, 'Parallax')) parallaxEls.push(el);
    });
  }
  var ticking = false;
  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () {
      ticking = false;
      var vh = window.innerHeight;
      batching = true;
      try {
        parallaxEls.forEach(function (el) {
          var host = el.parentElement;
          var r = host.getBoundingClientRect();
          if (r.bottom < -200 || r.top > vh + 200) return;
          var d = parseFloat(el.getAttribute('data-lm-depth')) || 0.2;
          var off = (r.top + r.height / 2 - vh / 2) * -d;
          write(function () { el.style.translate = '0 ' + off.toFixed(1) + 'px'; });
        });
        scrollFns.forEach(function (fn) { try { fn(vh); } catch (e) { /* one effect must not stop the rest */ } });
        var max = document.documentElement.scrollHeight - vh;
        var sp = (max > 0 ? Math.min(1, window.scrollY / max) : 0).toFixed(4);
        write(function () {
          if (progressBar) progressBar.style.transform = 'scaleX(' + sp + ')';
          for (var ti = 0; ti < toTops.length; ti++) toTops[ti].style.setProperty('--lm-scroll', sp);
        });
      } finally {
        batching = false;
        var q = writeQ; writeQ = [];
        for (var wi = 0; wi < q.length; wi++) { try { q[wi](); } catch (e) { /* keep going */ } }
      }
    });
  }

  /* ---------- Scroll progress bar + custom cursor (page-level) ---------- */
  var progressBar = null;
  var toTops = [];
  function initPageChrome() {
    toTops = Array.prototype.slice.call(document.querySelectorAll('[data-lm-totop]'));
    if (!document.querySelector('.lm')) return;
    if (!document.querySelector('.lm-progress')) {
      progressBar = document.createElement('div');
      progressBar.className = 'lm-progress';
      progressBar.setAttribute('aria-hidden', 'true');
      document.body.appendChild(progressBar);
    }
    if (deskFx.matches && !reduceMotion && document.querySelector('[data-lm-cursor]') && !document.querySelector('.lm-cursor')) {
      var ring = document.createElement('div');
      ring.className = 'lm-cursor';
      var dot = document.createElement('div');
      dot.className = 'lm-cursor-dot';
      [ring, dot].forEach(function (el) { el.setAttribute('aria-hidden', 'true'); document.body.appendChild(el); });
      var mx = -100, my = -100, rx = -100, ry = -100, following = false;
      // The ring eases after the pointer; the loop sleeps once it has caught up.
      function follow() {
        rx += (mx - rx) * 0.18; ry += (my - ry) * 0.18;
        ring.style.transform = 'translate(' + rx.toFixed(1) + 'px,' + ry.toFixed(1) + 'px)';
        if (Math.abs(mx - rx) + Math.abs(my - ry) > 0.3) requestAnimationFrame(follow); else following = false;
      }
      document.addEventListener('pointermove', function (e) {
        if (e.pointerType !== 'mouse') return;
        mx = e.clientX; my = e.clientY;
        if (!following) { following = true; requestAnimationFrame(follow); }
        dot.style.transform = 'translate(' + mx + 'px,' + my + 'px)';
        var t = e.target.closest && e.target.closest('a, button, [role="tab"], canvas[data-lm-globe], input, select, textarea');
        ring.classList.toggle('is-hover', !!t);
        ring.classList.toggle('is-grab', !!(t && t.tagName === 'CANVAS'));
      }, { passive: true });
      document.addEventListener('pointerdown', function () { ring.classList.add('is-down'); });
      document.addEventListener('pointerup', function () { ring.classList.remove('is-down'); });
    }
  }

  /* ---------- Boot ---------- */
  function init(root) {
    root = root || document;
    initReveal(root);
    initTabs(root);
    initSliders(root);
    initShowcase(root);
    initWave(root);
    initSilk(root);
    initGlobes(root);
    initChats(root);
    initRotators(root);
    initMagnetic(root);
    initSpots(root);
    initParallax(root);
    initHeroStack(root);
    initProcess(root);
    initJourney(root);
    initSparks(root);
    initWaveBgs(root);
    initFooterWord(root);
    initContactForms(root);
    initStackCards(root);
    initReel(root);
    initCatalog(root);
    initFill(root);
    initInset(root);
    initMarquees(root);
    initScramble(root);
    onScroll();
  }
  // CSS animations keep costing style work every frame even off screen, so sections that are
  // out of view get .lm-offscreen (lm-brand.css pauses every animation inside them).
  var pauseObserver = ('IntersectionObserver' in window)
    ? new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { e.target.classList.toggle('lm-offscreen', !e.isIntersecting); });
      }, { rootMargin: '120px 0px' })
    : null;
  function watchPause(s) {
    // The fixed header's and chat's wrappers scroll away while they stay on screen, so skip them.
    if (pauseObserver && !s.querySelector('[data-lm-header], .lm-header, [data-lm-assistant]')) pauseObserver.observe(s);
  }

  function boot() {
    initPageChrome();
    var sections = Array.prototype.slice.call(document.querySelectorAll('.shopify-section'));
    if (!sections.length) init();
    else {
      // Wire sections up one per task, in page order, so the first load is many short tasks
      // instead of one long one that blocks taps and scrolling.
      (function next() {
        var s = sections.shift();
        if (!s) return;
        init(s);
        watchPause(s);
        if (sections.length) setTimeout(next, 0);
      })();
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // Theme editor: re-run when a section is added or its settings change.
  document.addEventListener('shopify:section:load', function (e) { initPageChrome(); init(e.target); watchPause(e.target); });
  document.addEventListener('shopify:block:select', function (e) {
    var panel = e.target.closest && e.target.closest('[role="tabpanel"]');
    var wrap = panel && panel.closest('[data-lm-tabs]');
    if (wrap) {
      var idx = Array.prototype.indexOf.call(wrap.querySelectorAll('[role="tabpanel"]'), panel);
      var btn = wrap.querySelectorAll('[role="tab"]')[idx];
      if (btn) btn.click();
    }
    var screen = e.target.closest && e.target.closest('.lm-show__screen');
    var show = screen && screen.closest('[data-lm-show]');
    if (show) {
      var i = Array.prototype.indexOf.call(show.querySelectorAll('.lm-show__screen'), screen);
      var go = show.querySelectorAll('[data-show-go]')[i];
      if (go) go.click();
    }
  });
})();

/* Smooth mode: pause endless decorations that start after the switch (reveals add new ones). */
(function () {
  var html = document.documentElement;
  if (!document.getAnimations) return;
  function sweep() {
    if (!html.classList.contains('lm-smooth')) return;
    document.getAnimations().forEach(function (a) {
      try { if (a.playState === 'running' && a.effect && !(a.effect.target && a.effect.target.closest && a.effect.target.closest('.lm-chat, .lm-proj, [data-lm-film], .lm-loader')) && a.effect.getComputedTiming().iterations === Infinity) a.pause(); } catch (e) { /* ignore */ }
    });
  }
  var t = 0;
  window.addEventListener('scroll', function () { clearTimeout(t); t = setTimeout(sweep, 400); }, { passive: true });
  window.addEventListener('lm:smooth', sweep);
  window.addEventListener('load', function () { setTimeout(sweep, 500); });
})();
